import React, { useState, useEffect, useRef } from 'react'
import { useJumpLinks } from './JumpLinksContext'
import * as Styled from './styles/StickyJumpLinks.styles'

const StickyJumpLinks = ({ jumpLinksLabel, precomputedJumpLinks = [] }) => {
  const { jumpLinks } = useJumpLinks()
  const [activeLink, setActiveLink] = useState('')
  const [isSticky, setIsSticky] = useState(false)
  const isProgrammaticScrollingRef = useRef(false)

  // Use precomputed jump links if available, otherwise fall back to context
  const finalJumpLinks =
    precomputedJumpLinks.length > 0 ? precomputedJumpLinks : jumpLinks

  useEffect(() => {
    // Handle initial page load with hash
    if (finalJumpLinks.length > 0 && window.location.hash) {
      const hashId = window.location.hash.substring(1)
      const matchingLink = finalJumpLinks.find(link => link.id === hashId)
      if (matchingLink) {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          scrollToSection(hashId)
          setActiveLink(hashId)
        }, 500)
      }
    }
  }, [finalJumpLinks])

  useEffect(() => {
    if (finalJumpLinks.length === 0) return

    // Track which section is currently in view using Intersection Observer
    const observer = new IntersectionObserver(
      entries => {
        // Don't update active link during programmatic scrolling
        if (isProgrammaticScrollingRef.current) return

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.id
            setActiveLink(id)

            // Update URL hash - but only if it's different from current hash
            const currentHash = window.location.hash.substring(1)
            if (currentHash !== id) {
              const newUrl = `${window.location.pathname}${window.location.search}#${id}`
              window.history.replaceState(null, '', newUrl)
            }
          }
        })
      },
      {
        rootMargin: '-20% 0px -70% 0px', // Trigger when section is in the middle of viewport
        threshold: 0,
      }
    )

    // Observe all H2 elements
    finalJumpLinks.forEach(link => {
      const element = document.getElementById(link.id)
      if (element) {
        observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [finalJumpLinks])

  useEffect(() => {
    // Handle browser back/forward navigation
    const handlePopState = () => {
      if (window.location.hash) {
        const hashId = window.location.hash.substring(1)
        const matchingLink = finalJumpLinks.find(link => link.id === hashId)
        if (matchingLink) {
          scrollToSection(hashId)
          setActiveLink(hashId)
        }
      } else {
        setActiveLink('')
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [finalJumpLinks])

  useEffect(() => {
    if (finalJumpLinks.length === 0) return

    // Track sticky state
    const handleStickyTracking = () => {
      const stickyContainer = document.querySelector('[data-sticky-jump-links]')
      const contentRegion = document.querySelector('#ContentRegion')
      if (stickyContainer && contentRegion) {
        const rect = stickyContainer.getBoundingClientRect()
        const contentRect = contentRegion.getBoundingClientRect()

        // Check if the sticky container is positioned at the top of the ContentRegion
        // When sticky, the element's top should be at the ContentRegion's top
        const isStuckToTop =
          rect.top <= contentRect.top && contentRegion.scrollTop > 0
        setIsSticky(isStuckToTop)
      }
    }

    const contentRegion = document.querySelector('#ContentRegion')
    if (contentRegion) {
      contentRegion.addEventListener('scroll', handleStickyTracking, {
        passive: true,
      })
      handleStickyTracking() // Check initial state

      return () =>
        contentRegion.removeEventListener('scroll', handleStickyTracking)
    }
  }, [finalJumpLinks])

  const scrollToSection = id => {
    const element = document.getElementById(id)
    if (!element) return

    // Set programmatic scrolling flag to prevent Intersection Observer interference
    isProgrammaticScrollingRef.current = true

    // Get sticky menu height
    const stickyContainer = document.querySelector('[data-sticky-jump-links]')
    const stickyHeight = stickyContainer ? stickyContainer.offsetHeight : 0

    // Log element position before scroll
    const elementRectBefore = element.getBoundingClientRect()

    // Log current scroll position
    const contentRegion = document.querySelector('#ContentRegion')
    const windowScrollTop =
      window.pageYOffset || document.documentElement.scrollTop
    const contentScrollTop = contentRegion ? contentRegion.scrollTop : 0

    // Check for ads above the element and calculate potential height
    const adContainers = document.querySelectorAll('[id^="div-"]')
    let potentialAdHeight = 0
    let hasUnloadedAds = false

    const adsAbove = Array.from(adContainers).filter(container => {
      const containerRect = container.getBoundingClientRect()
      return (
        containerRect.top < elementRectBefore.top && containerRect.bottom > 0
      )
    })

    adsAbove.forEach((ad, index) => {
      const rect = ad.getBoundingClientRect()
      const hasIframe = !!ad.querySelector('iframe')
      const iframeLoaded =
        ad.querySelector('iframe')?.getAttribute('data-load-complete') ===
        'true'

      // If ad has iframe but hasn't loaded yet, estimate its potential height
      if (hasIframe && !iframeLoaded && rect.height === 0) {
        // Estimate ad height based on common ad sizes (90px is common)
        potentialAdHeight += 90
        hasUnloadedAds = true
      }

      // Also check for ad containers with no height but positioned above target
      // These might be ads that are still loading and haven't created iframes yet
      if (!hasIframe && rect.height === 0 && rect.top < elementRectBefore.top) {
        // Estimate ad height based on common ad sizes (90px is common)
        potentialAdHeight += 90
        hasUnloadedAds = true
      }
    })

    const executeScroll = () => {
      // Calculate total offset: just sticky menu height + small buffer
      const totalOffset = stickyHeight + 10

      // Temporarily set scroll-margin-top on this specific element
      const originalScrollMargin = element.style.scrollMarginTop
      element.style.scrollMarginTop = `${totalOffset}px`

      // First scroll to the element
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      // Log element position after first scroll
      setTimeout(() => {
        const elementRectAfter = element.getBoundingClientRect()

        const windowScrollTopAfter =
          window.pageYOffset || document.documentElement.scrollTop
        const contentScrollTopAfter = contentRegion
          ? contentRegion.scrollTop
          : 0

        // If there were unloaded ads, wait for layout changes then do correction
        if (hasUnloadedAds) {
          // Use ResizeObserver to detect when layout changes due to ad loads
          const resizeObserver = new ResizeObserver(entries => {
            // Disconnect the observer to prevent multiple corrections
            resizeObserver.disconnect()

            // Clear the fallback timeout
            if (fallbackTimeout) {
              clearTimeout(fallbackTimeout)
            }

            // Do a correction scroll to fine-tune the position
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })

            // Final position logging
            setTimeout(() => {
              const finalElementRect = element.getBoundingClientRect()

              const finalWindowScrollTop =
                window.pageYOffset || document.documentElement.scrollTop
              const finalContentScrollTop = contentRegion
                ? contentRegion.scrollTop
                : 0

              // Restore the original scroll-margin
              element.style.scrollMarginTop = originalScrollMargin

              // Clear programmatic scrolling flag and set correct active link
              isProgrammaticScrollingRef.current = false
              setActiveLink(id)
            }, 1000)
          })

          // Observe the ContentRegion for size changes
          if (contentRegion) {
            resizeObserver.observe(contentRegion)
          }

          // Also observe any ad containers above the element
          adsAbove.forEach(ad => {
            resizeObserver.observe(ad)
          })

          // Fallback timeout in case ResizeObserver doesn't fire
          const fallbackTimeout = setTimeout(() => {
            resizeObserver.disconnect()

            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })

            setTimeout(() => {
              element.style.scrollMarginTop = originalScrollMargin

              // Clear programmatic scrolling flag and set correct active link
              isProgrammaticScrollingRef.current = false
              setActiveLink(id)
            }, 1000)
          }, 2000) // 2 second fallback
        } else {
          // No unloaded ads, just log final position

          // Restore the original scroll-margin
          element.style.scrollMarginTop = originalScrollMargin

          // Clear programmatic scrolling flag and set correct active link
          isProgrammaticScrollingRef.current = false
          setActiveLink(id)
        }
      }, 1000)
    }

    // Execute the scroll immediately, then do a correction if needed
    executeScroll()
  }

  const handleLinkClick = id => {
    // Update URL hash
    const newUrl = `${window.location.pathname}${window.location.search}#${id}`
    window.history.pushState(null, '', newUrl)

    // Update active link immediately for better UX
    setActiveLink(id)

    // Scroll to section
    scrollToSection(id)
  }

  // Don't render anything if there are no jump links
  if (finalJumpLinks.length === 0) {
    return null
  }

  return (
    <Styled.StickyContainer data-sticky-jump-links isSticky={isSticky}>
      <Styled.JumpLinksWrapper>
        <Styled.JumpLinksLabel>{jumpLinksLabel}</Styled.JumpLinksLabel>
        {finalJumpLinks.map((link, index) => (
          <React.Fragment key={link.id}>
            <Styled.JumpLinkItem
              onClick={() => handleLinkClick(link.id)}
              isActive={activeLink === link.id}
              data-text={link.text}
            >
              {link.text}
            </Styled.JumpLinkItem>
            {index < finalJumpLinks.length - 1 && (
              <Styled.JumpLinkSeparator>|</Styled.JumpLinkSeparator>
            )}
          </React.Fragment>
        ))}
      </Styled.JumpLinksWrapper>
    </Styled.StickyContainer>
  )
}

export default StickyJumpLinks
